import { useEffect, useCallback } from "react";
import { useAudioEditor } from "../../../context/AudioEditorContext";
import { changeAudioSpeed } from "../../../utils/audioProcessing";
import { v4 as uuidv4 } from "uuid";
import { nocache } from "./utils";
import { AudioEditorTrackType } from "../../../types/types";

/**
 * Hook to register track speed processing actions in AudioEditorContext.
 */
const useAudioSpeedProcessor = () => {
    const { registerActions, tracks, setIsProcessing, newTrack, deleteTrack } = useAudioEditor();

    const processTrackSpeed = useCallback(
        async (trackIndex: number, speed: number, uploadToS3 = true): Promise<void> => {
            if (trackIndex < 0 || trackIndex >= tracks.length) {
                throw new Error("Invalid track index");
            }

            const track = tracks[trackIndex];
            if (!track || !track.url) {
                throw new Error("Track has no URL");
            }

            try {
                setIsProcessing(true);

                const response = await fetch(nocache(track.url));
                const audioBlob = await response.blob();

                const { url: processedUrl } = await changeAudioSpeed(audioBlob, speed, uploadToS3);

                // We need to ensure all properties needed for rendering are defined
                const processedTrack: AudioEditorTrackType = {
                    id: uuidv4(),
                    name: `${track.name} (${speed.toFixed(1)}x)`,
                    url: processedUrl,
                    description: `${track.description || ""} (Speed: ${speed.toFixed(1)}x)`,
                    // The audio is a new file, but it still stands for the same source asset:
                    // keep the provenance so the arrangement can be saved and replayed.
                    role: track.role,
                    sourceKind: track.sourceKind,
                    sourceId: track.sourceId,
                    sourceText: track.sourceText,
                    sourceVoice: track.sourceVoice,
                    sourceDialogLines: track.sourceDialogLines,
                };

                // Handle all the trimming and timing information
                if (track.cueIn !== undefined) {
                    processedTrack.cueIn = track.cueIn / speed;
                }

                if (track.cueOut !== undefined) {
                    processedTrack.cueOut = track.cueOut / speed;
                }

                // Preserve volume gain
                if (track.gain !== undefined) {
                    processedTrack.gain = track.gain;
                }

                // Preserve muted state
                if (track.muted !== undefined) {
                    processedTrack.muted = track.muted;
                }

                // Handle timing properties
                if (track.startTime !== undefined) {
                    processedTrack.startTime = track.startTime;

                    if (track.endTime !== undefined) {
                        const originalDuration = track.endTime - track.startTime;
                        const newDuration = originalDuration / speed;
                        processedTrack.endTime = track.startTime + newDuration;
                    }
                }

                // Instead of trying to transform the fade properties, which might be causing
                // rendering issues, we just omit them

                // Remove the old track first
                if (newTrack && deleteTrack) {
                    deleteTrack(trackIndex);
                    newTrack(processedTrack, trackIndex);
                }
            } catch (error) {
                console.error("Error processing track speed:", error);
                throw error;
            } finally {
                setIsProcessing(false);
            }
        },
        [tracks, setIsProcessing, newTrack, deleteTrack],
    );

    useEffect(() => {
        if (registerActions) {
            registerActions({ processTrackSpeed });
        }
    }, [registerActions, processTrackSpeed]);
};

export default useAudioSpeedProcessor;
